import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  TrendingUp,
  TrendingDown,
  ShieldCheck,
  Target,
  ListOrdered,
  Lightbulb,
  Info,
  CheckCircle2,
  XCircle,
  ExternalLink,
} from 'lucide-react';

import type { RegimeCommentary, AssetImpact } from '../types';
import { cn } from '../utils/formatters';

interface Props {
  commentary?: RegimeCommentary | null;
}

export const TreasuryCommentary: React.FC<Props> = ({ commentary }) => {
  if (!commentary) return null;

  const {
    regime_name,
    definition,
    equities,
    sectors,
    fixed_income,
    why_happening,
    recommendation,
    static_definitions,
  } = commentary;

  // 1. Dynamic Color Themes
  const isBull = regime_name.includes('Bull');
  const isBear = regime_name.includes('Bear');

  const themeColor = isBull
    ? 'text-emerald-600 dark:text-emerald-400'
    : isBear
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-indigo-600 dark:text-indigo-400';

  const bgTheme = isBull
    ? 'bg-emerald-50 dark:bg-emerald-900/10'
    : isBear
      ? 'bg-rose-50 dark:bg-rose-900/10'
      : 'bg-indigo-50 dark:bg-indigo-900/10';

  const borderTheme = isBull
    ? 'border-emerald-200 dark:border-emerald-900/50'
    : isBear
      ? 'border-rose-200 dark:border-rose-900/50'
      : 'border-indigo-200 dark:border-indigo-900/50';

  // Helper for Sector Rendering
  const renderSector = (impact: AssetImpact, isPositive: boolean) => (
    <div
      key={impact.name}
      className="flex flex-col mb-3 pb-3 border-b border-slate-100 dark:border-slate-800/50 last:border-0 last:pb-0 last:mb-0"
    >
      <div className="flex items-center gap-1.5 mb-1">
        {isPositive ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
        ) : (
          <XCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
        )}
        <span className="font-bold text-slate-800 dark:text-slate-200 text-sm">
          {impact.name}
        </span>
      </div>
      <span className="text-xs text-slate-500 dark:text-slate-400 ml-5 leading-relaxed">
        {impact.reason}
      </span>
    </div>
  );

  return (
    <div className="flex flex-col gap-6 w-full mb-8 animate-in fade-in duration-500">
      {/* --- 1. HEADER & DEFINITION --- */}
      <div className={cn('rounded-xl border shadow-sm overflow-hidden', borderTheme)}>
        <div
          className={cn(
            'px-6 py-5 border-b flex flex-col md:flex-row md:items-center justify-between gap-4',
            bgTheme,
            borderTheme,
          )}
        >
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'p-2 rounded-lg bg-white/60 dark:bg-slate-900/60 shadow-sm',
                themeColor,
              )}
            >
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                Strategic Market Regime Playbook for Today:{' '}
                <span className={themeColor}>{regime_name}</span>
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white/90 dark:bg-slate-800/90 px-3 py-1.5 rounded-md text-sm font-bold shadow-sm backdrop-blur-sm border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200">
            <Target className="w-4 h-4 text-indigo-500" />
            Stance: {recommendation.overall_stance}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-6">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Info className="w-4 h-4" /> Regime Definition
          </h3>
          <ul className="space-y-2">
            {definition.map((item, idx) => (
              <li
                key={idx}
                className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2"
              >
                <span
                  className={cn(
                    'mt-1.5 w-1.5 h-1.5 rounded-full shrink-0',
                    isBull
                      ? 'bg-emerald-500'
                      : isBear
                        ? 'bg-rose-500'
                        : 'bg-indigo-500',
                  )}
                />
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* --- 2. THREE-COLUMN MASONRY GRID (Equities, Sectors, Fixed Income) --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Equities Column */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3 mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              <a
                href="https://forge-ultra.ki-wealth.com/dashboard/stock-ranking"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <h3>Equities Outlook</h3>
                <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>
          <div className="text-sm font-bold mb-3 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-100 dark:border-slate-700 text-slate-700 dark:text-slate-300 w-fit">
            {equities.outlook}
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 leading-relaxed font-medium border-b border-slate-100 dark:border-slate-800 pb-4">
            {equities.summary}
          </p>
          <ul className="space-y-2.5 flex-1">
            {equities.reasons.map((reason, i) => (
              <li
                key={i}
                className="flex gap-2 text-sm text-slate-600 dark:text-slate-400 items-start"
              >
                <span className="text-blue-500 mt-1 shrink-0">&bull;</span>
                <span className="leading-relaxed">{reason}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Sectors Column */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm flex flex-col h-full">
          <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3 mb-4">
            <Target className="w-5 h-5 text-amber-500" />
            <Link
              to="/dashboard/market-analysis/sector-rotation"
              title="Open the Sector Rotation dashboard"
              className="group inline-flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-100 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
            >
              <h3 className="group-hover:underline underline-offset-2">
                Sector Rotation
              </h3>
              <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          </div>
          <div className="space-y-6 flex-1">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-500 mb-3 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" /> Outperform
              </h4>
              <div className="bg-emerald-50/50 dark:bg-emerald-900/10 rounded-lg p-3 border border-emerald-100 dark:border-emerald-900/30">
                {sectors.best.map((impact) => renderSector(impact, true))}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-rose-600 dark:text-rose-500 mb-3 flex items-center gap-1.5">
                <TrendingDown className="w-3.5 h-3.5" /> Underperform
              </h4>
              <div className="bg-rose-50/50 dark:bg-rose-900/10 rounded-lg p-3 border border-rose-100 dark:border-rose-900/30">
                {sectors.worst.map((impact) => renderSector(impact, false))}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Income Column */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm flex flex-col h-full">
          <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3 mb-4">
            <ShieldCheck className="w-5 h-5 text-slate-500" />
            <a
              href="https://forge-ultra.ki-wealth.com/dashboard/etf-ranking"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <h3>Fixed Income</h3>
              <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
          <div className="space-y-5 flex-1">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-500 mb-3 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Best Areas
              </h4>
              <ul className="space-y-2">
                {fixed_income.best_areas.map((area, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 mt-1.5" />
                    <span className="leading-snug">{area}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-rose-600 dark:text-rose-500 mb-3 flex items-center gap-1.5">
                <XCircle className="w-3.5 h-3.5" /> Worst Areas
              </h4>
              <ul className="space-y-2">
                {fixed_income.worst_areas.map((area, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 mt-1.5" />
                    <span className="leading-snug">{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              Credit Outlook
            </h4>
            <p className="text-sm text-slate-600 dark:text-slate-400 italic leading-relaxed">
              {fixed_income.credit_outlook}
            </p>
          </div>
        </div>
      </div>

      {/* --- 3. MACRO DRIVERS & STRATEGY ROW --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Why is this happening? */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-5 text-slate-800 dark:text-slate-200 border-b border-slate-100 dark:border-slate-800 pb-3">
            <ListOrdered className="w-5 h-5 text-indigo-500" />
            <h3 className="font-bold tracking-tight">Why is this happening?</h3>
          </div>

          <div className="space-y-6">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                Typical Sequence
              </h4>
              <div className="space-y-3 relative before:absolute before:inset-0 before:ml-2.5 before:-translate-x-px before:h-full before:w-0.5 before:bg-slate-200 dark:before:bg-slate-700 pl-8">
                {why_happening.sequence.map((step, idx) => {
                  // Strip any leading numbers like "1. " for a cleaner UI.
                  const stepText = step.replace(/^\d+\.\s*/, '');
                  return (
                    <div
                      key={idx}
                      className="relative text-sm text-slate-600 dark:text-slate-300 font-medium"
                    >
                      <div className="absolute w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 -left-8 top-0 flex items-center justify-center text-[10px] font-bold text-slate-500">
                        {idx + 1}
                      </div>
                      {stepText}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 mt-6">
                Macro Drivers
              </h4>
              <div className="flex flex-wrap gap-2">
                {why_happening.drivers.map((driver, idx) => (
                  <span
                    key={idx}
                    className="px-2.5 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-md text-xs font-semibold"
                  >
                    {driver}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Strategy Recommendation */}
        <div className="bg-slate-900 dark:bg-black border border-slate-800 rounded-xl p-6 shadow-lg flex flex-col justify-center text-white relative overflow-hidden">
          {/* Decorative glowing icon background */}
          <div className="absolute -top-10 -right-10 opacity-10">
            <Lightbulb className="w-48 h-48 text-indigo-500" />
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-6 border-b border-slate-800 pb-4">
              <Lightbulb className="w-6 h-6 text-indigo-400" />
              <h3 className="text-xl font-bold tracking-tight">
                Strategic Recommendation
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-3 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Favor
                </h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  {recommendation.favor.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1 h-1 bg-emerald-400 rounded-full mt-2 shrink-0" />
                      <span className="leading-tight">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-rose-400 mb-3 flex items-center gap-1.5">
                  <XCircle className="w-3.5 h-3.5" /> Avoid
                </h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  {recommendation.avoid.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1 h-1 bg-rose-400 rounded-full mt-2 shrink-0" />
                      <span className="leading-tight">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- 4. STATIC EDUCATIONAL DEFINITIONS --- */}
      <div className="bg-slate-50 dark:bg-slate-800/30 rounded-xl p-5 border border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
        <h4 className="font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <Info className="w-4 h-4" /> Core Principles and Definitions
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          {static_definitions.map((def, idx) => (
            <div key={idx} className="flex items-start gap-2 leading-relaxed">
              <span className="text-slate-400 dark:text-slate-500 mt-0.5 shrink-0">
                &bull;
              </span>
              {def}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
